
"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Bell,
  BriefcaseBusiness,
  Clock3,
  Check,
  Trash2,
  Inbox,
  X,
  CheckCircle2,
  XCircle,
  LoaderCircle,
  UserRound,
  MapPin,
  CalendarDays,
  UserRoundCheck,
} from "lucide-react";

import apiClient from "@/shared/services/api/apiClient";
import {
  getJobApprovalRequestById,
  approveJobRequest,
  rejectJobRequest,
} from "@/modules/jobs/api/job.api";
import {
  getActiveTeamMemberTransferRequests,
  decideActiveTeamMemberTransferRequest,
  type ActiveTeamMemberTransferRequest,
} from "@/modules/team/api/team.api";

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  eventType: string;
  entityType?: string | null;
  entityId?: string | null;
  jobId?: string | null;
  readAt?: string | null;
  createdAt: string;
  status?: string | null;
};

type ApprovalRequest = {
  id: string;
  requestType: "CREATE" | "UPDATE";
  jobId: string | null;
  requestedBy: string;
  reviewerId: string | null;
  submittedData: Record<string, any>;
  requestedStatus: string;
  status: string;
  comments: string | null;
  createdAt: string;
  requester?: {
    id: string;
    name: string;
    email?: string;
    role?: string;
  };
};

const formatLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object" && item !== null
          ? Object.values(item).filter(Boolean).join(" ")
          : String(item)
      )
      .join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => `${formatLabel(key)}: ${formatValue(item)}`)
      .join("\n");
  }

  return String(value);
};

const HIDDEN_FIELDS = new Set([
  "id",
  "createdBy",
  "updatedBy",
  "publishedBy",
  "approvalStatus",
  "approvalRequestedBy",
  "approvalReviewerId",
  "approvalRequestedAt",
  "approvalReviewedAt",
  "pendingChanges",
  "pendingStatus",
  "assignedToId",
  "assignedToIds",
  "createdAt",
  "updatedAt",
  "slug",
  "jobCode",
  "isActive",
]);

export default function Notifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedRequest, setSelectedRequest] =
    useState<ApprovalRequest | null>(null);
  const [selectedTransferRequest, setSelectedTransferRequest] =
    useState<ActiveTeamMemberTransferRequest | null>(null);
  const [canDecideTransfer, setCanDecideTransfer] = useState(false);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [comments, setComments] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const unreadCount = notifications.filter((item) => !item.readAt).length;

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await apiClient.get("/notifications");
      const result = data.data;

      setNotifications(
        Array.isArray(result) ? result : result.notifications ?? []
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Notifications could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const markRead = async (notificationId: string) => {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`);
      setNotifications((previous) =>
        previous.map((item) =>
          item.id === notificationId
            ? { ...item, readAt: new Date().toISOString() }
            : item
        )
      );
    } catch (err) {
      console.error("Unable to mark notification as read.", err);
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.patch("/notifications/read-all");
      setNotifications((previous) =>
        previous.map((item) => ({
          ...item,
          readAt: item.readAt || new Date().toISOString(),
        }))
      );
    } catch (err) {
      setError("Unable to mark all notifications as read.");
    }
  };

  const deleteNotification = async (notificationId: string) => {
    // Backend currently has no delete endpoint.
    // Do not remove the notification locally and imply it was deleted.
    setError("Notification deletion is not available in the backend yet.");
  };

  const openNotification = async (item: NotificationItem) => {
    setActionError("");
    setActionMessage("");
    setRejectMode(false);
    setRejectReason("");
    setComments("");

    if (!item.readAt) {
      await markRead(item.id);
    }

    const entityType = (item.entityType || "").toUpperCase();
    const eventType = (item.eventType || "").toUpperCase();
    const isActiveTransfer =
      entityType.includes("TRANSFER") || eventType.includes("TEAM_MEMBER_TRANSFER");

    if (isActiveTransfer) {
      if (!item.entityId) {
        setError("This transfer notification is missing its request ID.");
        return;
      }

      setSelectedRequest(null);
      setSelectedTransferRequest(null);
      setLoadingDetails(true);
      setError("");

      try {
        const response = await getActiveTeamMemberTransferRequests();
        const allRequests = [
          ...(response?.incomingRequests || []),
          ...(response?.myRequests || []),
        ];
        const transferRequest = allRequests.find(
          (request) => request.id === item.entityId
        );
        const isIncomingRequest = (response?.incomingRequests || []).some(
          (request) => request.id === item.entityId
        );

        if (!transferRequest) {
          throw new Error(
            "Transfer request was not found in the requests returned by the server. Verify that notification entityId contains the transfer request ID."
          );
        }

        setSelectedTransferRequest(transferRequest);
        setCanDecideTransfer(isIncomingRequest);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load the team member transfer request."
        );
      } finally {
        setLoadingDetails(false);
      }
      return;
    }

    if (
      entityType !== "JOB_APPROVAL_REQUEST" ||
      !item.entityId
    ) {
      setError(
        "This notification does not contain a supported approval request."
      );
      return;
    }

    setSelectedTransferRequest(null);
    setSelectedRequest(null);
    setLoadingDetails(true);
    setError("");

    try {
      const request = await getJobApprovalRequestById(item.entityId);
      setSelectedRequest(request);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Unable to load the submitted job details."
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    const confirmed = window.confirm(
      `Approve "${selectedRequest.submittedData?.title || "this job"}"?`
    );

    if (!confirmed) return;

    setActionLoading(true);
    setActionError("");
    setActionMessage("");

    try {
      await approveJobRequest(selectedRequest.id, comments || undefined);

      setActionMessage(
        selectedRequest.requestType === "CREATE"
          ? "Job approved and published successfully."
          : "Job changes approved successfully."
      );

      setSelectedRequest((previous) =>
        previous ? { ...previous, status: "APPROVED" } : previous
      );

      await loadNotifications();
    } catch (err: any) {
      setActionError(
        err?.response?.data?.message || "Unable to approve this request."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    const reason = rejectReason.trim();

    if (!reason) {
      setActionError("Please enter a rejection reason.");
      return;
    }

    setActionLoading(true);
    setActionError("");
    setActionMessage("");

    try {
      await rejectJobRequest(selectedRequest.id, reason);

      setActionMessage(
        "Request rejected. The requesting Team Member will be notified."
      );

      setSelectedRequest((previous) =>
        previous ? { ...previous, status: "REJECTED" } : previous
      );

      setRejectMode(false);
      await loadNotifications();
    } catch (err: any) {
      setActionError(
        err?.response?.data?.message || "Unable to reject this request."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferDecision = async (
    decision: "APPROVE" | "REJECT"
  ) => {
    if (!selectedTransferRequest) return;

    if (!canDecideTransfer || selectedTransferRequest.status !== "PENDING") {
      setActionError("Only the current HR owner can approve or reject a pending transfer request.");
      return;
    }

    const note = decision === "REJECT" ? rejectReason.trim() : comments.trim();
    if (decision === "REJECT" && !note) {
      setActionError("Please enter a rejection reason.");
      return;
    }

    const confirmed = window.confirm(
      decision === "APPROVE"
        ? `Approve transfer of ${selectedTransferRequest.memberName || "this team member"} to ${selectedTransferRequest.requestedHrAdminName || "the requesting HR Admin"}?`
        : `Reject transfer of ${selectedTransferRequest.memberName || "this team member"}?`
    );
    if (!confirmed) return;

    setActionLoading(true);
    setActionError("");
    setActionMessage("");

    try {
      const result = await decideActiveTeamMemberTransferRequest(
        selectedTransferRequest.id,
        decision,
        note || undefined
      );

      setSelectedTransferRequest((previous) =>
        previous
          ? {
              ...previous,
              status: decision === "APPROVE" ? "APPROVED" : "REJECTED",
              reviewerNote: note || previous.reviewerNote || null,
              reviewedAt: new Date().toISOString(),
            }
          : previous
      );
      setActionMessage(
        result?.message ||
          (decision === "APPROVE"
            ? "Transfer approved. Team member ownership has been updated."
            : "Transfer rejected. Team member ownership remains unchanged.")
      );
      await loadNotifications();
    } catch (err: any) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          `Unable to ${decision.toLowerCase()} this transfer request.`
      );
    } finally {
      setActionLoading(false);
    }
  };

  const filteredNotifications = notifications.filter((item) =>
    filter === "all" ? true : !item.readAt
  );

  const submittedData = selectedRequest?.submittedData ?? {};
  const canReview = selectedRequest?.status === "PENDING";

  return (
    <div className="w-full space-y-5 sm:space-y-6">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
              Notifications
            </h1>

            {unreadCount > 0 && (
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold text-blue-700">
                {unreadCount} unread
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-slate-500 sm:text-sm">
            Job approvals and other notifications.
          </p>
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="self-start rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 sm:self-auto sm:text-sm"
          >
            Mark All Read
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`rounded-xl px-4 py-2 text-xs font-semibold sm:text-sm ${
            filter === "all"
              ? "bg-blue-600 text-white"
              : "border border-slate-200 bg-white text-slate-600"
          }`}
        >
          All ({notifications.length})
        </button>

        <button
          onClick={() => setFilter("unread")}
          className={`rounded-xl px-4 py-2 text-xs font-semibold sm:text-sm ${
            filter === "unread"
              ? "bg-blue-600 text-white"
              : "border border-slate-200 bg-white text-slate-600"
          }`}
        >
          Unread ({unreadCount})
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
          <button
            onClick={() => setError("")}
            className="ml-2 font-bold underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-10 text-sm text-slate-500">
            <LoaderCircle className="h-5 w-5 animate-spin" />
            Loading notifications...
          </div>
        ) : filteredNotifications.length ? (
          filteredNotifications.map((item) => {
            const isApproval =
              item.entityType?.toUpperCase() === "JOB_APPROVAL_REQUEST";

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => void openNotification(item)}
                className={`group w-full rounded-2xl border p-4 text-left transition hover:border-blue-300 hover:shadow-sm sm:p-5 ${
                  !item.readAt
                    ? "border-blue-200 bg-blue-50/40"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3.5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                    {isApproval ? (
                      <BriefcaseBusiness className="h-5 w-5" />
                    ) : (
                      <Bell className="h-5 w-5" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-bold text-slate-900">
                        {item.title}
                      </h3>

                      {isApproval && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-800">
                          JOB APPROVAL
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {item.message}
                    </p>

                    <div className="mt-2.5 flex items-center gap-1.5 text-xs text-slate-400">
                      <Clock3 size={13} />
                      {new Date(item.createdAt).toLocaleString()}
                    </div>

                    {isApproval && (
                      <p className="mt-3 text-xs font-bold text-blue-700">
                        Click to review job details →
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    {!item.readAt && (
                      <span title="Unread" className="mt-1 h-2 w-2 rounded-full bg-blue-600" />
                    )}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
            <Inbox size={28} className="text-slate-400" />
            <h3 className="mt-3 text-sm font-bold text-slate-800">
              No notifications found
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {filter === "unread"
                ? "You have no unread notifications."
                : "Your notification inbox is empty."}
            </p>
          </div>
        )}
      </div>

      {loadingDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex items-center gap-3 rounded-2xl bg-white p-6 text-sm font-medium text-slate-700 shadow-xl">
            <LoaderCircle className="h-5 w-5 animate-spin" />
            Loading job details...
          </div>
        </div>
      )}

      {selectedRequest && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !actionLoading) {
              setSelectedRequest(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="approval-modal-title"
            className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 sm:p-6">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                    {selectedRequest.requestType} REQUEST
                  </span>

                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                      selectedRequest.status === "PENDING"
                        ? "bg-amber-100 text-amber-800"
                        : selectedRequest.status === "APPROVED"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {formatLabel(selectedRequest.status)}
                  </span>
                </div>

                <h2
                  id="approval-modal-title"
                  className="text-xl font-bold text-slate-900 sm:text-2xl"
                >
                  {submittedData.title || "Job approval details"}
                </h2>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <UserRound size={15} />
                    {selectedRequest.requester?.name || "Team Member"}
                  </span>

                  {selectedRequest.requester?.email && (
                    <span>{selectedRequest.requester.email}</span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRequest(null)}
                disabled={actionLoading}
                aria-label="Close popup"
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-5 sm:p-6">
              {actionMessage && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  {actionMessage}
                </div>
              )}

              {actionError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {actionError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(submittedData)
                  .filter(
                    ([key, value]) =>
                      !HIDDEN_FIELDS.has(key) &&
                      value !== null &&
                      value !== undefined &&
                      value !== ""
                  )
                  .map(([key, value]) => (
                    <div
                      key={key}
                      className={`min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-4 ${
                        ["description", "responsibilities", "requirements", "skills", "benefits"].includes(
                          key.toLowerCase()
                        )
                          ? "sm:col-span-2 lg:col-span-3"
                          : ""
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {formatLabel(key)}
                      </p>

                      <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-800">
                        {formatValue(value)}
                      </p>
                    </div>
                  ))}
              </div>

              {selectedRequest.comments && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h3 className="text-sm font-bold text-amber-900">
                    Review comments
                  </h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-amber-800">
                    {selectedRequest.comments}
                  </p>
                </div>
              )}

              {canReview && rejectMode && (
                <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-4">
                  <label
                    htmlFor="rejection-reason"
                    className="text-sm font-bold text-red-900"
                  >
                    Rejection reason <span className="text-red-600">*</span>
                  </label>

                  <textarea
                    id="rejection-reason"
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    placeholder="Explain why this job request is being rejected..."
                    rows={4}
                    required
                    maxLength={2000}
                    className="w-full rounded-xl border border-red-200 bg-white p-3 text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  />
                </div>
              )}

              {canReview && !rejectMode && (
                <div className="space-y-2">
                  <label
                    htmlFor="approval-comments"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Approval comments (optional)
                  </label>

                  <textarea
                    id="approval-comments"
                    value={comments}
                    onChange={(event) => setComments(event.target.value)}
                    placeholder="Add an optional comment..."
                    rows={2}
                    maxLength={2000}
                    className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
              <p className="text-xs text-slate-500">
                {canReview
                  ? "Review the submitted details before making a decision."
                  : "This request has already been reviewed."}
              </p>

              {canReview && (
                <div className="flex flex-wrap justify-end gap-2">
                  {rejectMode ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setRejectMode(false);
                          setRejectReason("");
                          setActionError("");
                        }}
                        disabled={actionLoading}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={() => void handleReject()}
                        disabled={actionLoading || !rejectReason.trim()}
                        className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {actionLoading && (
                          <LoaderCircle size={16} className="animate-spin" />
                        )}
                        Confirm Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setRejectMode(true);
                          setActionError("");
                        }}
                        disabled={actionLoading}
                        className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <XCircle size={17} />
                        Reject
                      </button>

                      <button
                        type="button"
                        onClick={() => void handleApprove()}
                        disabled={actionLoading}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {actionLoading ? (
                          <LoaderCircle size={17} className="animate-spin" />
                        ) : (
                          <CheckCircle2 size={17} />
                        )}
                        Approve
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedTransferRequest && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !actionLoading) {
              setSelectedTransferRequest(null);
              setActionError("");
              setActionMessage("");
              setRejectMode(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-modal-title"
            className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 sm:p-6">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700">
                  <UserRoundCheck size={14} /> TEAM MEMBER TRANSFER
                </span>
                <h2 id="transfer-modal-title" className="mt-3 text-xl font-bold text-slate-900 sm:text-2xl">
                  Transfer request details
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Review the requested ownership change before deciding.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedTransferRequest(null);
                  setActionError("");
                  setActionMessage("");
                  setRejectMode(false);
                }}
                disabled={actionLoading}
                aria-label="Close transfer details"
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-5 sm:p-6">
              {actionMessage && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  {actionMessage}
                </div>
              )}
              {actionError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {actionError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Team member</p>
                  <p className="mt-2 text-sm font-bold text-slate-900">{selectedTransferRequest.memberName || "Team member"}</p>
                  {selectedTransferRequest.memberEmail && <p className="mt-1 break-words text-sm text-slate-600">{selectedTransferRequest.memberEmail}</p>}
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Request status</p>
                  <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${selectedTransferRequest.status === "PENDING" ? "bg-amber-100 text-amber-800" : selectedTransferRequest.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {formatLabel(selectedTransferRequest.status)}
                  </span>
                  <p className="mt-2 text-xs text-slate-500">Submitted {new Date(selectedTransferRequest.createdAt).toLocaleString()}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current HR owner</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedTransferRequest.currentHrAdminName || "Not provided"}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Requested HR owner</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedTransferRequest.requestedHrAdminName || selectedTransferRequest.requestedByName || "Not provided"}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4 sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Requested by</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedTransferRequest.requestedByName || "HR Admin"}</p>
                  {selectedTransferRequest.requestedByEmail && <p className="mt-1 text-sm text-slate-600">{selectedTransferRequest.requestedByEmail}</p>}
                </div>
              </div>

              {selectedTransferRequest.reviewerNote && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Review note</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{selectedTransferRequest.reviewerNote}</p>
                </div>
              )}

              {selectedTransferRequest.status === "PENDING" && rejectMode && (
                <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-4">
                  <label htmlFor="transfer-rejection-reason" className="text-sm font-bold text-red-900">
                    Rejection reason <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="transfer-rejection-reason"
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    placeholder="Explain why this transfer is being rejected..."
                    rows={3}
                    maxLength={2000}
                    className="w-full rounded-xl border border-red-200 bg-white p-3 text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  />
                </div>
              )}
              {selectedTransferRequest.status === "PENDING" && !rejectMode && (
                <div className="space-y-2">
                  <label htmlFor="transfer-review-note" className="text-sm font-semibold text-slate-700">Review note (optional)</label>
                  <textarea
                    id="transfer-review-note"
                    value={comments}
                    onChange={(event) => setComments(event.target.value)}
                    placeholder="Add an optional review note..."
                    rows={2}
                    maxLength={2000}
                    className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
              <p className="text-xs text-slate-500">
                  {selectedTransferRequest.status !== "PENDING"
                  ? "This transfer request has already been reviewed."
                  : canDecideTransfer
                    ? "Only the current HR owner can approve or reject this request."
                    : "You submitted this request. You can view its status here."}
              </p>
              {selectedTransferRequest.status === "PENDING" && canDecideTransfer && (
                <div className="flex flex-wrap justify-end gap-2">
                  {rejectMode ? (
                    <>
                      <button type="button" onClick={() => { setRejectMode(false); setRejectReason(""); setActionError(""); }} disabled={actionLoading} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
                      <button type="button" onClick={() => void handleTransferDecision("REJECT")} disabled={actionLoading || !rejectReason.trim()} className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                        {actionLoading && <LoaderCircle size={16} className="animate-spin" />} Confirm Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => { setRejectMode(true); setActionError(""); }} disabled={actionLoading} className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"><XCircle size={17} /> Reject</button>
                      <button type="button" onClick={() => void handleTransferDecision("APPROVE")} disabled={actionLoading} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                        {actionLoading ? <LoaderCircle size={17} className="animate-spin" /> : <CheckCircle2 size={17} />} Approve
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
